import { groupLifeService, type GroupLifeStepName } from '@/services';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useQuoteParams } from '../../context/quote-params.context';

/**
 * Tiny helper that wraps `groupLifeService.saveStep` with a loading flag and
 * a shared error message. Each Group Life wizard step calls
 * `await saveStep('plan', payload)` right before invoking `onNext()`, so the
 * InsureMO proposal stays in sync with the local context state.
 *
 * If `policyId` is missing (rare — step 1 always creates it), the hook
 * resolves to success without calling the API so the wizard can still
 * advance during dev sessions.
 */
export function useSaveGroupLifeStep() {
  const { policyId } = useQuoteParams();
  const queryClient = useQueryClient();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async (
    step: GroupLifeStepName,
    payload: Record<string, unknown>,
  ): Promise<boolean> => {
    setError(null);
    if (!policyId) {
      console.warn(
        `[group-life] saveStep(${step}) skipped — policyId vazio. ` +
          'O step 1 (Estipulante) precisa ser concluído primeiro.',
      );
      return true;
    }
    setSaving(true);
    try {
      const result = await groupLifeService.saveStep({
        policyId,
        step,
        payload,
      });
      if (!result.success) {
        setError(result.error ?? 'Falha ao salvar no InsureMO');
        return false;
      }
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['group-life', 'quote-details', policyId],
        }),
        queryClient.invalidateQueries({
          queryKey: ['group-life', 'members-page', policyId],
        }),
        queryClient.invalidateQueries({
          queryKey: ['group-life', 'proposal-lives', policyId],
        }),
        queryClient.invalidateQueries({
          queryKey: ['pme-tarifacao', 'quote', policyId],
        }),
      ]);
      return true;
    } finally {
      setSaving(false);
    }
  };

  return { save, saving, error };
}
